import { useState, useCallback } from "react";


export type DialogKey = string;

/**
 * Hook générique pour gérer les états de plusieurs dialogues dans une application React.
 * Permet de contrôler l'ouverture, la fermeture et le basculement de l'état de chaque dialogue.
 * 
 * @template T - Type des clés de dialogue (généralement string)
 * @param {T[]} initialDialogs - Tableau des identifiants de dialogues à initialiser
 * @returns {Object} Objet contenant les contrôleurs de dialogues et méthodes utilitaires
 * @returns {Record<T, DialogController>} dialogs - Contrôleurs pour chaque dialogue
 * @returns {(dialog: T) => void} openDialog - Fonction pour ouvrir un dialogue spécifique
 * @returns {(dialog: T) => void} closeDialog - Fonction pour fermer un dialogue spécifique
 * @returns {(dialog: T) => void} toggleDialog - Fonction pour basculer l'état d'un dialogue
 * @returns {() => void} closeAllDialogs - Fonction pour fermer tous les dialogues
 * 
 * @example
 * const { dialogs } = useDialogs(['confirmation', 'settings']);
 * 
 * // Utilisation
 * <button onClick={dialogs.confirmation.open}>Ouvrir</button>
 * <Dialog open={dialogs.confirmation.isOpen} onClose={dialogs.confirmation.close}>
 *   Contenu du dialogue
 * </Dialog>
 */
export function useDialogs<T extends DialogKey>(initialDialogs: T[]) {
  // Etat pour chaque dialogue
  const [dialogStates, setDialogStates] = useState<Record<T, boolean>>(() => {
    const initialState = {} as Record<T, boolean>;
    initialDialogs.forEach((dialog) => {
      initialState[dialog] = false;
    });
    return initialState;
  });

  // Ouvrir un dialogue spécifique
  const openDialog = useCallback((dialog: T) => {
    setDialogStates((prev) => ({ ...prev, [dialog]: true }));
  }, []);

  // Fermer un dialogue spécifique
  const closeDialog = useCallback((dialog: T) => {
    setDialogStates((prev) => ({ ...prev, [dialog]: false }));
  }, []);

  // Basculer l'état d'un dialogue
  const toggleDialog = useCallback((dialog: T) => {
    setDialogStates((prev) => ({ ...prev, [dialog]: !prev[dialog] }));
  }, []);

  // Fermer tous les dialogues
  const closeAllDialogs = useCallback(() => {
    const closedState = {} as Record<T, boolean>;
    initialDialogs.forEach((dialog) => {
      closedState[dialog] = false;
    });
    setDialogStates(closedState);
  }, [initialDialogs]);

  // Création d'un objet avec les méthodes et états pour tous les dialogues
  const dialogs = initialDialogs.reduce((acc, dialog) => {
    acc[dialog] = {
      isOpen: dialogStates[dialog],
      open: () => openDialog(dialog),
      close: () => closeDialog(dialog),
      toggle: () => toggleDialog(dialog),
    };
    return acc;
  }, {} as Record<T, { isOpen: boolean; open: () => void; close: () => void; toggle: () => void }>);

  return {
    dialogs,
    openDialog,
    closeDialog,
    toggleDialog,
    closeAllDialogs,
  };
}
